import { Bell, X } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { Badge } from '@reflex/ui/components/ui/badge';
import { cn } from '@reflex/ui/lib/utils';
import type { Notification } from '../../shared/types.js';

export interface ReleaseGroupInboxRowProps {
  /** Newest release — drives the row's primary title. */
  latest: Notification;
  /** Other grouped releases, newest first. */
  others: Notification[];
  /** Highlighted when any grouped release is the active right-pane selection. */
  selected: boolean;
  /** Opens the latest release in the right pane. */
  onSelect: () => void;
  /** Dismisses every grouped release in one shot. */
  onDismissAll: () => void;
}

/**
 * Inbox sidebar variant of the release group row. Mirrors
 * `NotificationInboxRow` shape so the section reads consistently — but the
 * trailing X dismisses every release queued in the group, not just the
 * latest. The "+N" badge reassures the user that the older releases aren't
 * being lost: they're still in the inbox, just folded into this row until
 * the user acts.
 */
export function ReleaseGroupInboxRow({
  latest,
  others,
  selected,
  onSelect,
  onDismissAll,
}: ReleaseGroupInboxRowProps) {
  const totalCount = others.length + 1;
  return (
    <div
      className={cn(
        'group flex w-full min-w-0 items-center gap-2 rounded-md border border-transparent',
        selected
          ? 'border-selected-border bg-selected text-selected-foreground'
          : 'border-border bg-card hover:bg-accent/40 transition-colors',
      )}
      data-testid={`notification-release-group-${latest.id}`}
      data-group-size={totalCount}
      data-selected={selected || undefined}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-label={`Open notification: ${latest.title}`}
        className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left focus:outline-none"
      >
        <Bell className="icon-sm shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium">{latest.title}</span>
            <Badge variant="secondary" className="shrink-0 text-3xs">
              +{others.length}
            </Badge>
          </span>
          <span className="truncate text-xs text-muted-foreground">
            Also: {others.map((n) => n.title).join(', ')}
          </span>
        </span>
      </button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`Dismiss all ${totalCount} release notifications`}
        onClick={onDismissAll}
        className="mr-1 h-7 w-7 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
      >
        <X className="icon-sm" aria-hidden="true" />
      </Button>
    </div>
  );
}
